
import { Component, Prop, Vue } from 'vue-property-decorator';
// @ts-expect-error:
import auth from '@platform/authentication';
// @ts-expect-error:
import FeedbackService from '@platform/feedback-service';
import PasswordErrors from './PasswordErrors.vue';

export interface IPasswordHistoryError {
  code: string;
  message: string;
  httpStatusCode: number;
}

@Component({
  components: {
    PasswordErrors,
  },
})
// @ts-ignore
export default class PasswordModal extends Vue {
    @Prop({
      required: true,
      default: () => ({}),
      type: Object,
      // @ts-ignore
    }) readonly user;

    public errorData = {
      hasError: false,
      errorMessage: '',
    }

    public isZenviaEmail(email:string) {
      const zenviaEmailRegex = /^[a-zA-Z0-9._%+-]+@zenvia\.com$/;
      return zenviaEmailRegex.test(email);
    }

    public loading = false;

    public passwordStrength = '';

    public hasPasswordVisible = false;

    public hasPasswordConfirmVisible = false;

    public invalidPassword = false;

    public newPassword = '';

    public newPasswordConfirm = '';

    public passwordHistory = process.env.VUE_APP_PASSWORD_HISTORY;

    public isBlocked = false;

    public showPasswordModal = false;

    public passwordNotMatch = false;

    public passwordTouched = false;

    public passwordConfirmTouched = false;

    get isPasswordEmpty(): boolean {
      return this.passwordTouched && this.newPassword.trim() === '';
    }

    get isPasswordConfirmEmpty(): boolean {
      return this.passwordConfirmTouched && this.newPasswordConfirm.trim() === '';
    }

    public onPasswordInput() {
      if (!this.passwordTouched && this.newPassword.length > 0) {
        this.passwordTouched = true;
      }
    }

    public onPasswordBlur() {
      this.passwordTouched = true;
    }

    public onPasswordConfirmInput() {
      if (!this.passwordConfirmTouched && this.newPasswordConfirm.length > 0) {
        this.passwordConfirmTouched = true;
      }
    }

    public onPasswordConfirmBlur() {
      this.passwordConfirmTouched = true;
    }

    public onChangePassword() {
      this.invalidPassword = false;
      this.passwordNotMatch = false;
      this.errorData.hasError = false;
    }

    public checkPasswordEquality() {
      return this.newPassword.trim() === this.newPasswordConfirm.trim()
        && this.newPassword.trim() !== ''
        && this.newPasswordConfirm.trim() !== '';
    }

    public validatePassword(newPassword: string) {
      if (newPassword) {
        const letter = /[A-Za-z]/;
        const numberTest = /[0-9]/;
        const specialCharacter = /[!@#$%^&*)(+=._-]/;

        const hasLetter = letter.test(newPassword);
        const hasNumberCase = numberTest.test(newPassword);
        const hasSpecialCharacter = specialCharacter.test(newPassword);

        if (hasLetter && hasNumberCase && hasSpecialCharacter && newPassword.length >= 8) {
          this.passwordStrength = 'STRONG';
        } else if (
          (
            (hasNumberCase || hasSpecialCharacter) && (hasLetter && newPassword.length >= 8)
          ) || (hasLetter && this.newPassword.length >= 14)
        ) {
          this.passwordStrength = 'GOOD';
        } else {
          this.passwordStrength = 'WEAK';
        }
      }
    }

    public togglePassword(isConfirmPassword: boolean) {
      if (!isConfirmPassword) {
        this.hasPasswordVisible = !this.hasPasswordVisible;
      } else this.hasPasswordConfirmVisible = !this.hasPasswordConfirmVisible;
    }

    public clearPassword(isConfirmPassword: boolean) {
      if (!isConfirmPassword) {
        this.newPassword = '';
      } else this.newPasswordConfirm = '';
    }

    public async changePassword() {
      this.errorData.hasError = false;
      this.invalidPassword = false;
      this.passwordNotMatch = false;

      if (this.loading) return;

      this.passwordTouched = true;
      this.passwordConfirmTouched = true;

      if (this.newPassword.trim() === '' || this.newPasswordConfirm.trim() === '') {
        return;
      }

      if (!this.checkPasswordEquality()) {
        this.passwordNotMatch = true;
        return;
      }

      if (!this.checkRequirementPassword(this.newPassword)) {
        this.invalidPassword = true;
        return;
      }

      this.loading = true;
      try {
        await auth.changePassword(this.newPassword);
        FeedbackService.toastr.show('success', this.$t('ACCOUNT.PASSWORD_UPDATED_SUCCESS'));
        this.closePasswordModal();
        this.resetForm();
      } catch (error: any) {
        const { response: { data } } = error;
        const errorMessageCode = this.checkPasswordHistory(data);
        this.errorData.hasError = true;
        this.errorData.errorMessage = `${this.$t(`ACCOUNT.${errorMessageCode}`, { password_history: this.passwordHistory })}`;
      } finally {
        this.loading = false;
      }
    }

    private resetForm() {
      this.newPassword = '';
      this.newPasswordConfirm = '';
      this.passwordTouched = false;
      this.passwordConfirmTouched = false;
    }

    public checkPasswordHistory(data: IPasswordHistoryError): string {
      const { code, httpStatusCode } = data;

      if (httpStatusCode === 400 && code === 'PasswordHistoryError') {
        return 'PASSWORD_HISTORY_ERROR';
      }
      return 'PASSWORD_UPDATED_FAIL';
    }

    private checkRequirementPassword(password: string) {
      const rule = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%&*()/\-+=_^?])(?=.{8,})/;
      return rule.test(password);
    }

    public hasHistory() {
      return window.history.length > 2;
    }

    closePasswordModal(): void {
      this.$emit('closePasswordModal');
    }
}
